import { useContext, useMemo } from "react"
import { IShipUser } from "../../Types"
import { createViewData } from "../../App/ViewData"
import { useDataStore } from "../../Data"
import { ShipUserWriters, createStoreShipUserWriters } from "./writers"

// The data-acquisition layer's assembled crew value for the ship_users tab's
// presentational component (`UsersViewUI`), read via `useShipUsersData()`. Populated by
// `UsersDataView`, which reads the roster from the centralized store via `useDeps` and
// provides store-backed optimistic writers. `UsersViewUI` never touches the store itself
// (issue #1186, mirrors Day's `DayDataViewData`).
export interface ShipUsersData {
  // The crew roster. Always a resolved array (possibly empty) from the seeded/fetched
  // store; `undefined` (which drives the `<Loading/>` gate) is retained for standalone
  // renders that have no roster yet.
  shipUsers: IShipUser[] | undefined;
  leveledShipUsers: IShipUser[];
  unleveledShipUsers: IShipUser[];
  // The crew write surface (create/update/delete) the edit sub-views call via
  // `useShipUserWriters(...)`, backed by optimistic `DataStore.mutate` writes.
  writers: ShipUserWriters;
}

export const { Context: ShipUsersDataContext, useData: useShipUsersData } = createViewData<ShipUsersData>("ShipDataUsersView");

// The crew write surface for an edit sub-view. Returns the writers provided by the
// enclosing `UsersView` boundary when one is mounted. When rendered standalone (the edit
// views / overlays render outside any UsersView boundary -- e.g. their catalog entries,
// unit tests, or the re-invite/create overlays), it builds the same store-backed
// optimistic writers itself, so a standalone overlay writes through the store too
// (@mutle decision, issue #1186). `shipId` is passed explicitly
// rather than read from `GetShip()` so the standalone overlays -- which receive `ship` as a
// prop and render without a `ShipContext` -- resolve it too. In the app the `DataStore` is
// provided at the root, so `useDataStore()` always yields the real store.
export function useShipUserWriters(shipId: number): ShipUserWriters {
  const ctx = useContext(ShipUsersDataContext);
  const store = useDataStore();
  return useMemo(() => {
    if (ctx?.writers) {
      return ctx.writers;
    }
    return createStoreShipUserWriters(store, { shipId });
  }, [ctx?.writers, store, shipId]);
}
